/* ==========================================================================
   Alorae — base.js
   Foundation only: <alorae-element> base class, lazy-hydration helper,
   tiny utilities, fetch wrappers for cart/products endpoints.
   Loaded on every page via theme.liquid. Keep ≤ 4KB minified.
   Web Components for individual sections live in assets/section-<name>.js
   or assets/<component-name>.js and import nothing from libraries.
   ========================================================================== */

(() => {
  const w = window;
  w.alorae = w.alorae || {};
  const A = w.alorae;

  /* ---------- Config bootstrap ----------
     theme.liquid sets window.alorae.config (routes, strings, settings,
     money_format, etc.). This file does not re-declare it. */

  /* ---------- DOM utilities ---------- */
  A.qs  = (sel, root = document) => root.querySelector(sel);
  A.qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Debounce / throttle ---------- */
  A.debounce = (fn, wait = 200) => {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  A.throttle = (fn, wait = 100) => {
    let last = 0, t;
    return function (...args) {
      const now = Date.now();
      const remaining = wait - (now - last);
      if (remaining <= 0) {
        clearTimeout(t);
        last = now;
        fn.apply(this, args);
      } else if (!t) {
        t = setTimeout(() => {
          last = Date.now();
          t = null;
          fn.apply(this, args);
        }, remaining);
      }
    };
  };

  /* ---------- Money formatter ----------
     Uses Shopify's money_format string from theme config.
     Supports the common Shopify format tokens. */
  A.formatMoney = (cents, format) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || A.config?.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, decimal] = value.split('.');
    return fmt
      .replace('{{amount}}', `${whole}.${decimal}`)
      .replace('{{amount_no_decimals}}', whole)
      .replace('{{amount_with_comma_separator}}',
        `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimal}`)
      .replace('{{amount_no_decimals_with_comma_separator}}',
        whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  };

  /* ---------- Fetch helpers ---------- */
  const json = (r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  };

  A.api = {
    cart: {
      get:    ()           => fetch(A.config.routes.cart, { headers: { Accept: 'application/json' }}).then(json),
      add:    (form)       => fetch(A.config.routes.cartAdd, { method: 'POST', body: form }).then(json),
      change: (payload)    => fetch(A.config.routes.cartChange, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                                body: JSON.stringify(payload)
                              }).then(json),
    },
    recommendations: (productId, limit = 4) =>
      fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}`,
            { headers: { Accept: 'application/json' }}).then(json),
  };

  /* ---------- Event bus on document (light-touch) ---------- */
  A.emit = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(`alorae:${name}`, { detail, bubbles: true }));
  };
  A.on = (name, handler) => document.addEventListener(`alorae:${name}`, handler);
  A.off = (name, handler) => document.removeEventListener(`alorae:${name}`, handler);

  /* ---------- <alorae-element> base class ----------
     Every interactive component extends this.
     Subclasses MUST define `connect()` and may define `disconnect()`. */
  class AloraeElement extends HTMLElement {
    constructor() { super(); this._wired = false; }

    connectedCallback() {
      if (this._wired) return;
      this._wired = true;
      // Lazy-hydrate if author opted in.
      if (this.hasAttribute('lazy')) {
        this._lazyHydrate();
      } else {
        this.connect?.();
      }
    }

    disconnectedCallback() {
      this.disconnect?.();
      this._io?.disconnect();
    }

    _lazyHydrate() {
      const margin = this.getAttribute('lazy-margin') || '200px';
      this._io = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this._io.disconnect();
          this.connect?.();
        }
      }, { rootMargin: margin });
      this._io.observe(this);
    }

    // Convenience scoped event helpers (component-local events).
    fire(name, detail = {}) {
      this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
    }
  }

  A.Element = AloraeElement;
  // Backwards-compat name (in case something already uses HTMLAloraeElement)
  if (!customElements.get('alorae-element')) {
    // Tag is NOT auto-registered — subclasses do that themselves.
  }
})();
